import { UnreachableCaseError } from "client_legacy/ndaw/errors";
import { localize } from "client_legacy/ndaw/culture";
import {
	ToDoListModel,
	listToDoLists,
	ToDoListIdentifier,
	ToDoListIdentifierKind
} from "client/Api/Workspace/LiveInformation/ToDos/toDoListLiveInformation";
import {
	ToDoListItem,
	markToDoAsComplete,
	createToDo,
	getLiveInformation,
	LiveInformationRequestModel,
	LiveInformationResponseModel,
	ItemsResult,
	Interests,
	convertListIdentifierToModel,
	ItemsResultStatus,
	ListItemId,
	setPreferences,
	getPreferences,
	ListItemIdModel
} from "client/Api/Workspace/LiveInformation/ToDos/toDoLiveInformation";
import { Guid } from "client_legacy/ndaw/guid";
import { alertBar } from "client_legacy/ndaw/alertBar";
import {
	CacheItem,
	InterestedParty,
	LiveInformationProvider,
	SuccessfulCacheItem
} from "client_legacy/Workspace/LiveInformation/liveInformationProvider";
import { wrapPromiseWithCancellation, ensureNotNil } from "client/Misc/utils";
import { NormalizedMap } from "client_legacy/ndaw/normalizedMap";
import { createAbortError } from "client_legacy/ndaw/cancellation";
import { formatForDto } from "client/DateAndTime/dateTimeUtils";
import { userProfileStore } from "client/Features/Settings/UserProfile/userProfileStore";

interface ToDoLiveInformationProviderOptions {
	intervalInSeconds: number;
}

export interface InterestedToDoParty extends InterestedParty<Guid, ToDoListIdentifier>{
	updateItems?(result: ItemsResult): void;
	pendingTaskCompletion(id: ListItemId): void;
	taskCompletionFailed(id: ListItemId): void;
	taskCompleted(id: ListItemId): void;
}

export class ToDoLiveInformationProvider extends LiveInformationProvider<
	ToDoLiveInformationProviderOptions,
	Guid,
	ToDoListIdentifier,
	Interests,
	InterestedToDoParty,
	LiveInformationResponseModel
>{
	private _listRetrievalPromise: Promise<ToDoListModel[]> | undefined;

	private readonly _preferences: Map<number, ToDoListIdentifier>;
	private _preferenceRetrievalPromise: Promise<void> | undefined;

	constructor(options: ToDoLiveInformationProviderOptions) {
		super(options);
		this._preferences = new Map<number, ToDoListIdentifier>();
	}

	protected async getDataFromBackend(
		entries: readonly [ToDoListIdentifier, Interests][]
	): Promise<readonly [ToDoListIdentifier, LiveInformationResponseModel][]> {
		const { userTimezone } = userProfileStore.getState();

		const requestModels: LiveInformationRequestModel[] = entries.map(
			([resourceId, interests]) => {
				const requestModel: LiveInformationRequestModel = {
					list: convertListIdentifierToModel(resourceId),
					interestedIn: interests
				};

				return requestModel;
			});

		const utcOffset: string = formatForDto(new Date(), userTimezone);
		const responses: LiveInformationResponseModel[] = await getLiveInformation(
			utcOffset,
			requestModels
		);

		return responses.map(
			(x) => [x.list, x]
		);
	}

	protected updateCacheItem(
		cacheItem: SuccessfulCacheItem<Interests, LiveInformationResponseModel>,
		response: [Interests, LiveInformationResponseModel]
	): void {
		const [retrievedInterests, model] = response;
		if (retrievedInterests.items) {
			cacheItem.retrievedInterests.items = true;
			cacheItem.data.data.items = model.data.items;
		}
	}

	protected informParty(
		party: InterestedToDoParty,
		cacheItem: [Interests, LiveInformationResponseModel]
	): void {
		const [retrievedInterests, response] = cacheItem;
		const interestedIn: Interests = this.getInterestsForEnsuredParty(party);

		if (interestedIn.items && retrievedInterests.items) {
			if (party.updateItems) {
				party.updateItems(
					ensureNotNil(
						response.data.items
					)
				);
			}
		}
	}

	public async createTask(list: ToDoListIdentifier, name: string): Promise<void> {
		await createToDo(list, name);

		await this.updateCacheFromTheBackendAsync(
			[
				[list, { items: true }]
			],
			{ clearCacheBeforeFilling: false }
		);

		this.updateAllParties();
	}

	public async completeTask(
		list: ToDoListIdentifier,
		id: ListItemIdModel
	): Promise<void> {
		const partiesToUpdateAfterCompletion: InterestedToDoParty[] = this.getPartiesWithTaskInList(id);
		for (const party of partiesToUpdateAfterCompletion) {
			party.pendingTaskCompletion(id);
		}

		try {
			await markToDoAsComplete({
				itemId: id,
				listIdentifier: convertListIdentifierToModel(list)
			});

		} catch (e) {
			console.error(e);
			alertBar.showTextAlert(localize("_GeneralErrorMessage"), "error");
			for (const party of partiesToUpdateAfterCompletion) {
				party.taskCompletionFailed(id);
			}
			return;
		}

		for (const cacheItem of this._cache.values()) {
			if (cacheItem.wasGlobalFailure) {
				continue;
			}

			const cachedResult: ItemsResult | undefined = cacheItem.data.data.items;
			if (!cachedResult) {
				continue;
			}

			if (cachedResult.status !== ItemsResultStatus.Success) {
				continue;
			}

			const items: ToDoListItem[] = cachedResult.items;

			const indexOnPage: number = items.findIndex((task) => areIdsEqual(task.id, id));
			if (indexOnPage === -1) {
				continue;
			}

			items.splice(indexOnPage, 1);
		}

		for (const party of partiesToUpdateAfterCompletion) {
			const cacheItem: CacheItem<Interests, LiveInformationResponseModel> | undefined = this._cache.get(party.resourceId);
			if (!cacheItem
				|| cacheItem.wasGlobalFailure
				|| !cacheItem.data.data.items
				|| cacheItem.data.data.items.status !== ItemsResultStatus.Success
			) {
				// In the mean time the cache was deleted or updated
				continue;
			}

			party.taskCompleted(id);
		}
	}

	private getPartiesWithTaskInList(id: ListItemId): InterestedToDoParty[] {
		const partiesWithTaskInList: InterestedToDoParty[] = [];
		for (const { party } of this._registeredParties.values()) {
			const cacheItem: CacheItem<Interests, LiveInformationResponseModel> | undefined = this._cache.get(party.resourceId);

			if (!cacheItem || cacheItem.wasGlobalFailure) {
				continue;
			}

			const cachedResult: ItemsResult | undefined = cacheItem.data.data.items;
			if (!cachedResult) {
				continue;
			}

			if (cachedResult.status !== ItemsResultStatus.Success) {
				continue;
			}

			const { items } = cachedResult;

			const indexOnPage: number = items.findIndex(
				(task) => areIdsEqual(task.id, id)
			);

			if (indexOnPage === -1) {
				continue;
			}

			partiesWithTaskInList.push(party);
		}

		return partiesWithTaskInList;
	}

	public listToDoLists(signal?: AbortSignal): Promise<ToDoListModel[]> {
		if (!this._listRetrievalPromise) {
			this._listRetrievalPromise = listToDoLists();
		}

		return this._listRetrievalPromise
			.then((x) => {
				if (signal?.aborted) {
					throw createAbortError();
				}

				return x;
			});
	}

	public async getPreference(
		tileId: number,
		signal?: AbortSignal
	): Promise<ToDoListIdentifier | undefined>
	{
		if (!this._preferences.has(tileId)) {
			await this.loadPreferencesAsync(signal);
		}

		return this._preferences.get(tileId);
	}

	public setPreference(
		tileId: number,
		listId: ToDoListIdentifier,
		signal?: AbortSignal
	): Promise<void> {
		this._preferences.set(tileId, listId);

		return setPreferences(
			tileId,
			{ listId: convertListIdentifierToModel(listId) },
			signal
		);
	}

	protected override createResourceMap<TValue>(): Map<ToDoListIdentifier, TValue> {
		return new NormalizedMap<ToDoListIdentifier, TValue>(
			normalizeKey
		);
	}

	private loadPreferencesAsync(signal?: AbortSignal): Promise<void>
	{
		if (!this._preferenceRetrievalPromise) {
			// The main promise we never want to cancel as it is shared between all tiles
			this._preferenceRetrievalPromise = getPreferences()
				.then(
					(x): void => {
						for (const preference of x) {
							if (this._preferences.has(preference.tileId)) {
								// In case it was already set by the frontend
								continue;
							}

							this._preferences.set(
								preference.tileId,
								preference.listId
							);
						}
					}
				);
		}

		return wrapPromiseWithCancellation(
			this._preferenceRetrievalPromise,
			signal
		);
	}
}

function normalizeKey(key: ToDoListIdentifier): string {
	switch (key.kind) {
		case ToDoListIdentifierKind.Default:
			return `${key.kind}`;
		case ToDoListIdentifierKind.Specific:
			return `${key.kind}_${key.id}`;
		case ToDoListIdentifierKind.WellKnown:
			return `${key.kind}_${key.wellKnownListKind}`;
		case ToDoListIdentifierKind.Planner:
			return `${key.kind}`;
		default:
			throw new UnreachableCaseError(key);
	}
}

export function areIdsEqual(a: ListItemId, b: ListItemId): boolean {
	return a.kind === b.kind && a.id === b.id;
}

const BASE_URL: string = "https://to-do.office.com";
const OFFICE_365_ACCOUNT_AUTHENTICATION = 2;

export function buildFullUrlWithSingleSignOn(relativeUrl: string) {
	return `${BASE_URL}${relativeUrl}?auth=${OFFICE_365_ACCOUNT_AUTHENTICATION}`;
}

export function encodeGraphIdAccordingToToDoApp(id: string) {
	// The ToDo app uses Base64Url instead of Base64 as ids, so we update
	// the ids to follow it.
	// '+' is replaced with '_'
	// '/' is replaced with '-'
	return id.replace(/\+/g, "_")
		.replace(/\//g, "-");
}
